import { FatTransactionsPageAllocationQuery, FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { Loader } from '@/components/loaders/loader';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import TablePagination from '@/components/table/tablePagination';
import { IPagination, ISort } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useMutation } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import { Dispatch, SetStateAction, useState } from 'react';
import { NavigateOptions } from 'react-router-dom';
import styled from 'styled-components';
import { columns } from '../constants';
import EditAmount from '../modals/editAmount';
import MarkAsInvestorApproved from '../modals/markAsInvestorApproved';
import MarkAsSentToInvestor from '../modals/markAsSentToInvestor';
import MarkAsSignedByInvestor from '../modals/markAsSignedByInvestor';
import MarkAsSignedByManager from '../modals/markAsSignedByManager';
import ResendSignaturePacket from '../modals/resendSignaturePacket';
import ReviewSignaturePacket from '../modals/reviewSignaturePacket';
import StartOver from '../modals/startOver';
import Terminate from '../modals/terminate';
import TransactionCompleted from '../modals/transactionCompleted';
import { ModalWindowType } from '../modals/types';
import { ALLOCATION_QUERY, EXECUTE_ACTION_MUTATION } from '../queries';
import { TableBody } from './tableBody';
import MobileTable from './tableMobile';

interface TableProps {
  allocations: (FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][0] & { updated?: boolean })[];
  allocationTransactionsLoading: boolean;
  sort: ISort;
  paginationData: IPagination;
  setSort: (value: ISort) => void;
  openPage: (path: string, options?: NavigateOptions) => void;
  allocationTransactionsRefetch: () => void;
  setAllocations: Dispatch<
    SetStateAction<(FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][0] & { updated?: boolean })[]>
  >;
  onChangePaginationData: (limit: number, offset: number) => void;
  operationWorkflowTransitionTaskSummariesRefetch: () => void;
}

export type AllocationWithUpdated = FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number] & {
  updated?: boolean;
};

const Table = ({
  allocations,
  allocationTransactionsLoading,
  sort,
  paginationData,
  setSort,
  openPage,
  allocationTransactionsRefetch,
  setAllocations,
  onChangePaginationData,
  operationWorkflowTransitionTaskSummariesRefetch
}: TableProps) => {
  const { isMobile } = useResponsive();
  const { user } = useAuth();

  const [modalWindow, setModalWindow] = useState<{ isOpen: boolean; type: ModalWindowType }>({
    isOpen: false,
    type: 'amount'
  });
  const [currentRow, setCurrentRow] = useState<AllocationWithUpdated | null>(null);
  const [showModalLoader, setShowModalLoader] = useState(false);

  const [executeAction] = useMutation(EXECUTE_ACTION_MUTATION);

  const startFlashRowAnimation = (allocationId: string) => {
    const updatedAllocations = allocations.map((allocation) => {
      if (allocation.id === allocationId) {
        return { ...allocation, updated: true }; // use this for animation
      }
      return { ...allocation };
    });

    setAllocations(updatedAllocations as any);
  };

  const updateLocalAllocation = (updatedAllocation: FatTransactionsPageAllocationQuery['Allocation']) => {
    const updatedAllocations = allocations.reduce<AllocationWithUpdated[]>((updatedAllocations, allocation) => {
      if (allocation.id === updatedAllocation.id) {
        if (updatedAllocation.allocationStatus === 'Outstanding') {
          return updatedAllocations.concat({
            ...allocation,
            ...updatedAllocation,
            updated: false // use this for animation
          });
        } else {
          return updatedAllocations;
        }
      } else {
        return updatedAllocations.concat({
          ...allocation,
          updated: false // use this for animation
        });
      }
    }, []);
    setAllocations(updatedAllocations as any);
  };

  const openModalWindow = (type: ModalWindowType, row: AllocationWithUpdated) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
    setCurrentRow(row);
  };

  const closeModalWindow = () => setModalWindow({ ...modalWindow, isOpen: false });

  const viewSignaturePacket = async (row: AllocationWithUpdated) => {
    try {
      setShowModalLoader(true);
      const { data, headers } = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/allocation-operations/${row?.id}/execute-action/viewSignaturePacket`,

        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([data], { type: headers['content-type'] }));
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
    } finally {
      setShowModalLoader(false);
    }
  };

  const executeSignaturePacket = async (row: AllocationWithUpdated) => {
    if (!row?.subscriptionDocument?.id) return;

    try {
      setShowModalLoader(true);

      const { data } = await executeAction({
        variables: {
          data: {
            allocationId: row.id,
            action: 'executeSignaturePacket',
            actionDataPayload: {
              subscriptionDocumentId: row.subscriptionDocument.id
            }
          }
        }
      });

      const url = data.executeAction.parsedResponse;
      window.open(url, '_self');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setShowModalLoader(false);
    }
  };

  const removeFromList = async (row: AllocationWithUpdated) => {
    try {
      startFlashRowAnimation(row.id);

      await executeAction({
        variables: {
          data: {
            allocationId: row.id,
            action: 'removeFromList'
          }
        }
      });

      openModalWindow('transactionCompleted', row);
      operationWorkflowTransitionTaskSummariesRefetch();

      const { data: allocationData } = await client.query({
        query: ALLOCATION_QUERY,
        variables: {
          id: row.id
        }
      });

      updateLocalAllocation(allocationData.Allocation);
    } catch (error) {
      console.error(error);
    }
  };

  const goBackToPreviousStep = async (row: AllocationWithUpdated) => {
    try {
      startFlashRowAnimation(row.id);

      await executeAction({
        variables: {
          data: {
            allocationId: row.id,
            action: 'goBackToPreviousStep'
          }
        }
      });

      operationWorkflowTransitionTaskSummariesRefetch();

      const { data: allocationData } = await client.query({
        query: ALLOCATION_QUERY,
        variables: {
          id: row.id
        }
      });

      updateLocalAllocation(allocationData.Allocation);
    } catch (error) {
      console.error(error);
    }
  };

  const actionsByStatus = (selectedItemName: string, row: AllocationWithUpdated) => {
    const { id, investment } = row;
    const dialogs = [
      'terminate',
      'startOver',
      'reviewSignaturePacket',
      'markAsSignedByInvestor',
      'markAsSignedByManager',
      'resendSignaturePacket',
      'markAsInvestorApproved',
      'markAsSentToInvestor'
    ];

    if (dialogs.includes(selectedItemName)) {
      openModalWindow(selectedItemName as ModalWindowType, row);
      return;
    }

    switch (selectedItemName) {
      case 'generateSignaturePacket':
        openPage(`generateSignaturePacket/${id}?backToTitle=Transactions`);
        break;
      case 'viewTransactionDetails':
        openPage(`transactionDetails/${id}?backToTitle=Transactions`);
        break;
      case 'forwardDealDocuments':
        openPage(`forwardDealDocuments/${investment?.id}?backToTitle=Transactions`, { state: { allocationId: id } });
        break;
      case 'viewSignaturePacket':
        viewSignaturePacket(row);
        break;
      case 'executeSignaturePacket':
        executeSignaturePacket(row);
        break;
      case 'removeFromList':
        removeFromList(row);
        break;
      case 'sendDealDocumentsToInvestor':
        openPage(`forwardDealDocuments/${investment?.id}?backToTitle=Transactions`, { state: { allocationId: id, forwardToInvestor: true } });
        break;
      case 'goBackToPreviousStep':
        goBackToPreviousStep(row);
        break;
      default:
        break;
    }
  };

  const filteredColumns = () => {
    if (user && user.tenant.type === TenantType.ADVISORY_FIRM) {
      return columns.map((column) => {
        if (column.key !== 'ADVISORY_FIRM') {
          return column;
        }
        return { ...column, width: 0, title: '' };
      });
    }

    return columns;
  };

  return (
    <>
      {showModalLoader && (
        <ModalWrappedContent
          title="Please Wait"
          isOpen={showModalLoader}
          onClose={() => {}}
          showCloseIcon={false}
          showCancelButton={false}
          isUseOutsideClick={false}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {modalWindow.isOpen && modalWindow.type === 'reviewSignaturePacket' && (
        <ReviewSignaturePacket isOpen={modalWindow.isOpen} currentRow={currentRow} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'startOver' && (
        <StartOver
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'markAsSignedByInvestor' && (
        <MarkAsSignedByInvestor
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'markAsSignedByManager' && (
        <MarkAsSignedByManager
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'amount' && (
        <EditAmount
          isOpen={modalWindow.isOpen}
          allocation={currentRow}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'terminate' && (
        <Terminate
          isOpen={modalWindow.isOpen}
          id={currentRow?.id ?? ''}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'resendSignaturePacket' && (
        <ResendSignaturePacket isOpen={modalWindow.isOpen} currentRow={currentRow} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'markAsSentToInvestor' && (
        <MarkAsSentToInvestor
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'markAsInvestorApproved' && (
        <MarkAsInvestorApproved
          isOpen={modalWindow.isOpen}
          currentRow={currentRow}
          onClose={closeModalWindow}
          updateLocalAllocation={updateLocalAllocation}
          startFlashRowAnimation={startFlashRowAnimation}
          operationWorkflowTransitionTaskSummariesRefetch={operationWorkflowTransitionTaskSummariesRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'transactionCompleted' && (
        <TransactionCompleted isOpen={modalWindow.isOpen} onClose={closeModalWindow} />
      )}
      <TableWrapper padding="0 0 30px">
        {isMobile ? (
          <>
            {allocationTransactionsLoading ? (
              <Loader />
            ) : (
              <MobileTable
                recommendationsRows={allocations}
                currentRow={currentRow}
                openModalWindow={openModalWindow}
                actionsByStatus={actionsByStatus}
                openPage={openPage}
              />
            )}
          </>
        ) : (
          <>
            <TableHeader columns={filteredColumns()} refetch={allocationTransactionsRefetch} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              {allocationTransactionsLoading ? (
                <Loader />
              ) : (
                <>
                  <TableBody
                    data={allocations}
                    columns={filteredColumns()}
                    currentRow={currentRow}
                    openPage={openPage}
                    openModalWindow={openModalWindow}
                    actionsByStatus={actionsByStatus}
                  />
                  <PaginationWrap>
                    <TablePagination
                      savePagination={onChangePaginationData}
                      paginationValues={paginationData}
                      refetch={allocationTransactionsRefetch}
                    />
                  </PaginationWrap>
                </>
              )}
            </TableBodyWrapper>
          </>
        )}
      </TableWrapper>
    </>
  );
};

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

export default Table;
